import { useMemo } from "react";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import { useUserMsp } from "../hooks/useUserMsp";
import { canAccessUsageAnalytics } from "../utils/horizonRoles";
import { isTechnicianDashboardLdEnabled } from "../utils/technicianDashboardAccess";
import { isTicketAnalysisReportLdEnabled } from "../utils/ticketAnalysisReportAccess";
import { isTicketLifecycleLdEnabled } from "../utils/ticketLifecycleDashboardAccess";
import { isUsageInsightsLdEnabled } from "../utils/usageInsightsAccess";
import { buildInsightsTabs, SectionTabDef } from "./sectionTabDefs";

export function useInsightsNavigation(): { tabs: SectionTabDef[]; panelIds: string[] } {
  const { userMspInfo } = useUserMsp();
  const flags = useFeatureFlag() as Record<string, unknown>;
  const theoAnalytics = flags["theoanalytics"] !== false;
  const usageInsights =
    isUsageInsightsLdEnabled(flags) && canAccessUsageAnalytics(userMspInfo?.role);

  const tabs = useMemo(
    () =>
      buildInsightsTabs({
        theoAnalytics,
        technician: isTechnicianDashboardLdEnabled(flags),
        ticketAnalysis: isTicketAnalysisReportLdEnabled(flags),
        usageInsights,
        ticketLifecycle: isTicketLifecycleLdEnabled(flags),
        showUsage: canAccessUsageAnalytics(userMspInfo?.role),
      }),
    // Flag helpers read the whole flags object; depend on it directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theoAnalytics, usageInsights, flags, userMspInfo?.role]
  );

  const panelIds = useMemo(
    () => tabs.filter((tab) => tab.kind === "panel").map((tab) => tab.id),
    [tabs]
  );

  return { tabs, panelIds };
}
